import {
	createContext,
	lazy,
	type ReactNode,
	Suspense,
	useContext,
	useEffect,
	useMemo,
	useState,
	useSyncExternalStore,
} from "react";

/**
 * What a module puts into the search palette.
 *
 * Deliberately opaque to the shell: the Agent's lists are the Agent's types,
 * and the shell's only job is to hold them between the column that computes
 * them and the palette that renders them. The two are siblings under
 * `AppShell` — the navigation is a slot, not a child of the screen — so they
 * cannot hand each other props.
 */
export type SearchContribution = Record<string, unknown>;

interface SearchState {
	open: boolean;
	setOpen: (open: boolean) => void;
}

const Search = createContext<SearchState | null>(null);

/**
 * The contribution lives outside React, and that is not an optimisation.
 *
 * It was a `useState` in the provider, which made a loop: the column that
 * contributes also reads the context, so writing re-rendered it, which rebuilt
 * the lists it had just handed over, which wrote again. React caught it as
 * "Maximum update depth exceeded" and stopped rendering the Agent's screens
 * altogether. A store only the palette subscribes to has no such edge.
 */
let current: SearchContribution | null = null;
const listeners = new Set<() => void>();

function publish(next: SearchContribution | null) {
	if (current === next) return;
	current = next;
	for (const listener of listeners) listener();
}

function subscribe(listener: () => void) {
	listeners.add(listener);
	return () => void listeners.delete(listener);
}

/** What a module has offered, for the palette alone. */
export function useContribution(): SearchContribution | null {
	return useSyncExternalStore(
		subscribe,
		() => current,
		() => null,
	);
}

/**
 * The palette itself, which was the Agent's and is now everybody's.
 *
 * Lazy, and mounted only once somebody has asked for it: its index is built
 * from the block registry, which is megabytes, and a reader who never presses
 * ⌘K should not pay for it.
 */
const Palette = lazy(() => import("@/client/search"));

/**
 * The palette's open state, and the channel a module fills it through.
 *
 * It lives in the shell because the control that opens it does. It was the
 * Agent's alone — rendered by its column, reachable only from its screens, and
 * offering only its own destinations — so a reader in Analytics had no search
 * at all.
 */
export function SearchProvider({ children }: { children: ReactNode }) {
	const [open, setOpen] = useState(false);

	// ⌘K from anywhere in the brand. The Agent registers the same key in its own
	// command table and its canvas opens the palette from a third place; those
	// end at this state too, bridged by the column that is mounted wherever they
	// can happen.
	useEffect(() => {
		const onKey = (event: KeyboardEvent) => {
			if (event.key.toLowerCase() !== "k" || !(event.metaKey || event.ctrlKey)) return;
			event.preventDefault();
			setOpen((was) => !was);
		};
		window.addEventListener("keydown", onKey);
		return () => window.removeEventListener("keydown", onKey);
	}, []);

	const value = useMemo(() => ({ open, setOpen }), [open]);
	return <Search.Provider value={value}>{children}</Search.Provider>;
}

export function useSearch(): SearchState {
	const state = useContext(Search);
	if (!state) throw new Error("useSearch must be used within SearchProvider");
	return state;
}

/**
 * Hands the palette what this module can offer, for as long as it is mounted.
 *
 * Cleared on unmount rather than left behind: crossing into Analytics takes the
 * Agent's workflows and chats off the screen, and a palette still offering them
 * is offering rows whose lists nothing is keeping up to date.
 */
export function useContributeSearch(contribution: SearchContribution) {
	useEffect(() => {
		publish(contribution);
		return () => publish(null);
	}, [contribution]);
}

/** Mounted by the shell, once anybody has asked for it. */
export function SearchPalette() {
	const { open } = useSearch();
	const contribution = useContribution();
	// Kept mounted after the first open: the palette holds its own recent-query
	// state, and a remount would drop it.
	const [everOpened, setEverOpened] = useState(false);
	useEffect(() => {
		if (open) setEverOpened(true);
	}, [open]);
	if (!everOpened && !contribution) return null;
	return (
		<Suspense fallback={null}>
			<Palette />
		</Suspense>
	);
}
